import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Bot, BellRing, Send, Loader2, Building, AlertCircle, Calculator } from "lucide-react";
import { searchJudicial, watchProceso, type JudicialSearchResult } from "@/services/scraperApi";
import { useAuth } from "@/contexts/AuthContext";
import { AuthGateModal } from "./AuthGateModal";
import { API_BASE_URL } from "@/config";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { PerjuiciosTrasladoPensionalForm } from "@/pages/dashboard/calculadoras/components/LaboralCalculators";

export const HomeInteractiveDemo: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Modal Auth Gate State
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"proceso" | "chatbot">("proceso");
  const [modalMeta, setModalMeta] = useState<{ radicado?: string; despacho?: string }>({});

  // --- TAB 1: BÚSQUEDA DE PROCESOS ---
  const [radicadoInput, setRadicadoInput] = useState("");
  const [isSearchingProc, setIsSearchingProc] = useState(false);
  const [procResult, setProcResult] = useState<JudicialSearchResult | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearchProceso = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const clean = radicadoInput.trim();
    if (!clean) return;

    setIsSearchingProc(true);
    setSearchError(null);
    setProcResult(null);

    try {
      const isDigitsOnly = /^\d+$/.test(clean);
      const res = await searchJudicial({
        query: clean,
        type: isDigitsOnly ? "radicado" : "nombre",
        sources: ["cpnu"],
      });

      if (res && res.results && res.results.length > 0) {
        setProcResult(res.results[0]);
      } else {
        setSearchError("No se encontraron registros para este criterio. Verifica los dígitos del radicado o el nombre.");
      }
    } catch (err: any) {
      setSearchError(err?.message || "No fue posible conectar con el servicio judicial en este momento.");
    } finally {
      setIsSearchingProc(false);
    }
  };

  const handleVigilarClick = async () => {
    if (!procResult) return;
    if (!user) {
      sessionStorage.setItem(
        "pending_proceso_watch",
        JSON.stringify({
          radicado: procResult.radicado,
          fuente: procResult.fuente || "CPNU",
          metadata: procResult,
        })
      );
      setModalType("proceso");
      setModalMeta({ radicado: procResult.radicado, despacho: procResult.despacho });
      setAuthModalOpen(true);
      return;
    }

    try {
      await watchProceso(procResult.radicado, procResult.fuente || "CPNU", procResult, user.id);
      toast({ title: "Proceso vigilado", description: `Radicado ${procResult.radicado} añadido a tu despacho.` });
      navigate("/dashboard/despachos/procesos", { state: { tab: "vigilancia" } });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  // --- TAB 2: CHATBOT SARAI DEMO ---
  const [chatMessages, setChatMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([
    {
      role: "assistant",
      content:
        "Hola. Soy SARAI, tu asistente legal especializado en Colombia. Pregúntame sobre cualquier código, término procesal o jurisprudencia de Altas Cortes.",
    },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [demoQueriesUsed, setDemoQueriesUsed] = useState(() => {
    return parseInt(localStorage.getItem("sarai_demo_queries_used") || "0", 10);
  });

  const handleSendChat = async (questionText?: string) => {
    const q = (questionText || chatInput).trim();
    if (!q || isAsking) return;

    if (!user && demoQueriesUsed >= 2) {
      setModalType("chatbot");
      setAuthModalOpen(true);
      return;
    }

    const nextUserMsg = { role: "user" as const, content: q };
    const updatedMessages = [...chatMessages, nextUserMsg];
    setChatMessages(updatedMessages);
    setChatInput("");
    setIsAsking(true);

    try {
      const baseApi = (API_BASE_URL || import.meta.env.VITE_API_BASE_URL || "http://localhost:8001").replace(/\/$/, "");
      const endpoint = `${baseApi}/api/v1/ask/public`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: q,
          history: updatedMessages.slice(-4),
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          setModalType("chatbot");
          setAuthModalOpen(true);
          return;
        }
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      let content = data?.response?.content || "No fue posible generar una respuesta en este momento.";

      // Limpieza defensiva en cliente por si algún fragmento JSON residual de clarification_form escapa
      const jsonStart = content.search(/\{\s*"title"\s*:\s*"[^"]*"/);
      if (jsonStart !== -1) {
        content = content.slice(0, jsonStart).trim();
      }
      content = content.replace(/```(?:clarification_form|json)?\s*```/gi, "").trim();

      setChatMessages((prev) => [...prev, { role: "assistant", content }]);

      if (!user) {
        const newCount = demoQueriesUsed + 1;
        setDemoQueriesUsed(newCount);
        localStorage.setItem("sarai_demo_queries_used", newCount.toString());
      }
    } catch (err) {
      console.error("Error en demo chat:", err);
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Ocurrió una interrupción temporal al conectar con el motor de respuestas. Por favor reintenta en unos instantes.",
        },
      ]);
    } finally {
      setIsAsking(false);
    }
  };

  return (
    <section className="py-12 px-4 bg-slate-50/70 dark:bg-slate-900/40 border-y border-slate-200/80 dark:border-slate-800 relative">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <Badge variant="outline" className="mb-2 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200">
            Prueba en Vivo Sin Registro
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            Experimenta el Poder de SARAI en Acción
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-xl mx-auto">
            Consulta expedientes judiciales reales o haz preguntas jurídicas con citación normativa al instante.
          </p>
        </div>

        <Tabs defaultValue="procesos" className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-1 sm:grid-cols-3 mb-6 h-auto sm:h-12 p-1.5 bg-slate-200/70 dark:bg-slate-800 rounded-2xl gap-1">
            <TabsTrigger value="procesos" className="rounded-xl font-bold flex items-center justify-center gap-2 text-xs sm:text-sm py-2">
              <Search className="w-4 h-4" />
              <span>Búsqueda Procesos</span>
            </TabsTrigger>
            <TabsTrigger value="chatbot" className="rounded-xl font-bold flex items-center justify-center gap-2 text-xs sm:text-sm py-2">
              <Bot className="w-4 h-4" />
              <span>Asistente Legal IA</span>
            </TabsTrigger>
            <TabsTrigger value="calculadora-csj" className="rounded-xl font-bold flex items-center justify-center gap-2 text-xs sm:text-sm py-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Calculator className="w-4 h-4" />
              <span>Calculadora CSJ Traslado</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: BÚSQUEDA PROCESOS */}
          <TabsContent value="procesos">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xl shadow-slate-900/5">
              <form onSubmit={handleSearchProceso} className="flex flex-col sm:flex-row gap-2.5 mb-4">
                <div className="relative flex-1">
                  <Input
                    placeholder="Ingresa número de radicado (23 dígitos) o nombre de la parte..."
                    value={radicadoInput}
                    onChange={(e) => setRadicadoInput(e.target.value)}
                    className="h-12 pl-11 rounded-xl text-sm font-medium"
                  />
                  <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                </div>
                <Button
                  type="submit"
                  disabled={isSearchingProc || !radicadoInput.trim()}
                  className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2"
                >
                  {isSearchingProc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span>Consultar Proceso</span>
                </Button>
              </form>

              {searchError && (
                <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 text-amber-800 dark:text-amber-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{searchError}</span>
                </div>
              )}

              {procResult && (
                <div className="mt-4 p-5 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/30 dark:bg-blue-950/20">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-600 text-white font-mono text-[11px]">
                          {procResult.fuente || "CPNU"}
                        </Badge>
                        <span className="font-mono font-bold text-sm text-slate-900 dark:text-white">
                          {procResult.radicado}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                        <Building className="w-3.5 h-3.5" />
                        {procResult.despacho || "Juzgado / Tribunal"}
                      </p>
                    </div>

                    <Button
                      onClick={handleVigilarClick}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl h-10 px-4 text-xs flex items-center gap-2 shadow-sm"
                    >
                      <BellRing className="w-4 h-4" />
                      <span>Vigilar este Proceso</span>
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-xs">
                    <div className="p-3 bg-white dark:bg-slate-800/80 rounded-lg border border-slate-100 dark:border-slate-800">
                      <strong className="block text-slate-400 uppercase tracking-wider text-[10px] mb-1">
                        Sujetos Procesales
                      </strong>
                      <p className="text-slate-800 dark:text-slate-200">
                        {procResult.demandante ? `Dte: ${procResult.demandante}` : ""}{" "}
                        {procResult.demandado ? `| Ddo: ${procResult.demandado}` : ""}
                        {!procResult.demandante && !procResult.demandado && "Información disponible en el expediente"}
                      </p>
                    </div>
                    <div className="p-3 bg-white dark:bg-slate-800/80 rounded-lg border border-slate-100 dark:border-slate-800">
                      <strong className="block text-slate-400 uppercase tracking-wider text-[10px] mb-1">
                        Tipo de Proceso
                      </strong>
                      <p className="text-slate-800 dark:text-slate-200 font-medium">
                        {procResult.tipo_proceso || "Proceso Ordinario / Ejecutivo"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB 2: CHATBOT SARAI */}
          <TabsContent value="chatbot">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xl shadow-slate-900/5">
              {!user && (
                <div className="mb-3 flex justify-between items-center text-xs text-slate-500 dark:text-slate-400">
                  <span>Prueba gratuita de demostración</span>
                  <Badge variant="secondary" className="font-semibold text-[11px]">
                    {Math.max(0, 2 - demoQueriesUsed)} de 2 consultas restantes
                  </Badge>
                </div>
              )}

              {/* Chat messages box */}
              <div className="h-80 overflow-y-auto space-y-3 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800 mb-3 text-xs sm:text-sm">
                {chatMessages.map((m, idx) => (
                  <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                        m.role === "user"
                          ? "bg-blue-600 text-white rounded-br-none"
                          : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-none shadow-sm"
                      }`}
                    >
                      {m.role === "user" ? (
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      ) : (
                        <div className="prose prose-sm dark:prose-invert max-w-none text-xs sm:text-sm leading-relaxed prose-p:my-1.5 prose-headings:my-2 prose-headings:font-bold prose-ul:my-1.5 prose-li:my-0.5">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {m.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isAsking && (
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 py-2.5 text-xs text-slate-500 flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />
                      <span>SARAI está consultando la base jurisprudencial...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Suggested Questions */}
              {demoQueriesUsed < 2 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[
                    "¿Término de prescripción de la acción cambiaria en pagarés?",
                    "¿Requisitos para estabilidad laboral reforzada?",
                    "¿Cuándo procede recurso de casación laboral?",
                  ].map((sugg, i) => (
                    <button
                      key={i}
                      onClick={() => handleSendChat(sugg)}
                      disabled={isAsking}
                      className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors text-left"
                    >
                      💡 {sugg}
                    </button>
                  ))}
                </div>
              )}

              {/* Chat input */}
              <div className="flex gap-2">
                <Input
                  placeholder={
                    !user && demoQueriesUsed >= 2
                      ? "Límite de prueba alcanzado. Regístrate gratis para continuar..."
                      : "Escribe tu consulta legal..."
                  }
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendChat()}
                  disabled={isAsking || (!user && demoQueriesUsed >= 2)}
                  className="h-11 rounded-xl text-xs sm:text-sm"
                />
                <Button
                  onClick={() => handleSendChat()}
                  disabled={isAsking || !chatInput.trim() || (!user && demoQueriesUsed >= 2)}
                  className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>

              {!user && demoQueriesUsed >= 2 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-xl flex items-center justify-between">
                  <span className="text-xs text-blue-800 dark:text-blue-300 font-medium">
                    ¿Deseas continuar esta consulta con tus propios documentos?
                  </span>
                  <Button
                    onClick={() => {
                      setModalType("chatbot");
                      setAuthModalOpen(true);
                    }}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg"
                  >
                    Crear Cuenta Gratuita
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB 3: CALCULADORA CSJ TRASLADO PENSIONAL EN EL HOME */}
          <TabsContent value="calculadora-csj">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-6 shadow-xl shadow-slate-900/5">
              <PerjuiciosTrasladoPensionalForm />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AuthGateModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        type={modalType}
        metadata={modalMeta}
      />
    </section>
  );
};

